import React, { useEffect, useState } from 'react';
import { UsersTable, UsersTableBody, UsersTableBodyColumn, UsersTableHead, UsersTableHeadColumn, UsersTableRow, Pill } from './style';
import axios from 'axios';
import AuthService from '../../../AuthService';
import Zoom from 'react-medium-image-zoom';
import Select from 'react-select';
import { Input, Loader } from '../../Login/style';
import Button from '../../../Components/Miscellaneous/Button';
import { toast } from 'react-toastify';
import CurrencyFormat from 'react-currency-format';
import { FiDownload, FiPrinter } from 'react-icons/fi';

export default function Stocks() {
    // Estados para productos y paginación
    const [products, setProducts] = useState([]);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    // Estados para filtros
    const [filters, setFilters] = useState({
        supplier: '',
        minStock: '',
        maxStock: '',
        category: '',
        reference: '',
        name: '',
        page: 1,
        limit: 100,
        sortBy: 'stock',
        sortOrder: 'asc'
    });

    // Lista de proveedores disponibles
    const [availableSuppliers, setAvailableSuppliers] = useState([]);

    // Fetch data con filtros
    const fetchData = async (filterParams = filters) => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams();

            // Añadir parámetros solo si tienen valor
            if (filterParams.supplier) queryParams.append('supplier', filterParams.supplier);
            if (filterParams.minStock !== '') queryParams.append('minStock', filterParams.minStock);
            if (filterParams.maxStock !== '') queryParams.append('maxStock', filterParams.maxStock);
            if (filterParams.category) queryParams.append('category', filterParams.category);
            if (filterParams.reference) queryParams.append('reference', filterParams.reference);
            if (filterParams.name) queryParams.append('name', filterParams.name);
            queryParams.append('page', filterParams.page);
            queryParams.append('limit', filterParams.limit);
            queryParams.append('sortBy', filterParams.sortBy);
            queryParams.append('sortOrder', filterParams.sortOrder);

            const response = await axios.get(`https://api.rb.beeapp.es/admin/stocks?${queryParams.toString()}`, {
                headers: {
                    Authorization: 'Bearer ' + AuthService.getToken()
                }
            });

            const data = response.data;
            setProducts(data.products || []);
            setPagination(data.pagination || {});
            setAvailableSuppliers(data.filters?.availableSuppliers || []);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching stocks:', error);
            toast.error('Error al cargar los productos');
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Manejar cambios en filtros
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Aplicar filtros
    const applyFilters = () => {
        const newFilters = { ...filters, page: 1 };
        setFilters(newFilters);
        fetchData(newFilters);
    };

    // Resetear filtros
    const resetFilters = () => {
        const defaultFilters = {
            supplier: '',
            minStock: '',
            maxStock: '',
            category: '',
            reference: '',
            name: '',
            page: 1,
            limit: 100,
            sortBy: 'stock',
            sortOrder: 'asc'
        };
        setFilters(defaultFilters);
        fetchData(defaultFilters);
    };

    // Manejar selección de productos
    const handleSelectProduct = (productId) => {
        if (selectedProducts.includes(productId)) {
            setSelectedProducts(selectedProducts.filter(id => id !== productId));
        } else {
            setSelectedProducts([...selectedProducts, productId]);
        }
    };

    // Seleccionar/Deseleccionar todos
    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedProducts([]);
        } else {
            setSelectedProducts(products.map(p => p._id));
        }
        setSelectAll(!selectAll);
    };

    // Exportar a CSV
    const exportToCSV = () => {
        const selectedData = products.filter(p => selectedProducts.includes(p._id));
        if (selectedData.length === 0) {
            toast.warning('Selecciona al menos un producto para exportar');
            return;
        }

        const headers = ['Referencia', 'Producto', 'Proveedor', 'Stock', 'Precio'];
        const csvContent = [
            headers.join(';'),
            ...selectedData.map(p => [
                p.reference || '',
                p.name || '',
                p.supplier || '',
                p.stock || 0,
                p.price || 0
            ].join(';'))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `stock_productos_${new Date().getTime()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success('Exportado a CSV correctamente');
    };

    // Imprimir productos seleccionados
    const printProducts = () => {
        const selectedData = products.filter(p => selectedProducts.includes(p._id));
        if (selectedData.length === 0) {
            toast.warning('Selecciona al menos un producto para imprimir');
            return;
        }

        const printWindow = window.open('', '_blank');

        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Listado de Productos - Stock</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        padding: 10px;
                        margin: 0;
                        font-size: 11px;
                    }
                    h1 {
                        color: #028322;
                        text-align: center;
                        margin-bottom: 10px;
                        font-size: 16px;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-top: 10px;
                        font-size: 10px;
                    }
                    th {
                        background-color: #028322;
                        color: white;
                        padding: 6px 8px;
                        text-align: left;
                        font-weight: 600;
                        font-size: 10px;
                    }
                    td {
                        padding: 5px 8px;
                        border-bottom: 1px solid #e0e0e0;
                        font-size: 10px;
                    }
                    tr:nth-child(even) {
                        background-color: #f5f5f5;
                    }
                    .stock-low {
                        color: #dc3545;
                        font-weight: 600;
                    }
                    .stock-ok {
                        color: #028322;
                        font-weight: 600;
                    }
                    @media print {
                        body {
                            padding: 5px;
                        }
                        h1 {
                            font-size: 14px;
                            margin-bottom: 8px;
                        }
                        table {
                            font-size: 9px;
                        }
                        th, td {
                            font-size: 9px;
                            padding: 4px 6px;
                        }
                    }
                </style>
            </head>
            <body>
                <h1>Listado de Productos - Control de Stock</h1>
                <table>
                    <thead>
                        <tr>
                            <th>Referencia</th>
                            <th>Producto</th>
                            <th>Proveedor</th>
                            <th>Stock</th>
                            <th>Precio</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${selectedData.map(p => `
                            <tr>
                                <td>${p.reference || '-'}</td>
                                <td>${p.name || '-'}</td>
                                <td>${p.supplier || '-'}</td>
                                <td class="${p.stock < 10 ? 'stock-low' : 'stock-ok'}">${p.stock || 0}</td>
                                <td>${p.price ? p.price.toFixed(2) + '€' : '-'}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            </body>
            </html>
        `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();

        printWindow.onload = function() {
            printWindow.focus();
            printWindow.print();
        };
    };

    const supplierOptions = availableSuppliers.map(s => ({ value: s, label: s }));
    supplierOptions.unshift({ value: '', label: 'Todos' });

    const sortByOptions = [
        { value: 'stock', label: 'Stock' },
        { value: 'name', label: 'Nombre' },
        { value: 'reference', label: 'Referencia' }
    ];

    const sortOrderOptions = [
        { value: 'asc', label: 'Ascendente' },
        { value: 'desc', label: 'Descendente' }
    ];

    const limitOptions = [
        { value: 50, label: '50' },
        { value: 100, label: '100' },
        { value: 200, label: '200' },
        { value: 500, label: '500' }
    ];

    return (
        <>
            {loading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 100,
                }}>
                    <div style={{
                        backgroundColor: '#fff',
                        padding: '20px',
                        borderRadius: '5px',
                        width: 'auto',
                        textAlign: 'center',
                    }}>
                        <Loader />
                    </div>
                </div>
            )}

            <div style={{ padding: '20px' }}>
                {/* Título de Filtros */}
                <h2 style={{
                    color: '#028322',
                    fontSize: '1.5rem',
                    marginBottom: '20px',
                    fontWeight: '600'
                }}>
                    Filtros
                </h2>

                {/* Panel de Filtros */}
                <div style={{
                    backgroundColor: '#f0f9f4',
                    padding: '20px',
                    borderRadius: '8px',
                    marginBottom: '20px',
                    border: '1px solid #d1f2e0'
                }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '15px',
                        marginBottom: '15px'
                    }}>
                        {/* Proveedor */}
                        <div>
                            <label style={{ fontSize: '14px', color: '#555', fontWeight: '500', display: 'block', marginBottom: '5px' }}>
                                Proveedor
                            </label>
                            <Select
                                options={supplierOptions}
                                value={supplierOptions.find(o => o.value === filters.supplier)}
                                onChange={(option) => handleFilterChange('supplier', option.value)}
                                placeholder="Todos"
                            />
                        </div>

                        {/* Stock Mínimo */}
                        <div>
                            <label style={{ fontSize: '14px', color: '#555', fontWeight: '500', display: 'block', marginBottom: '5px' }}>
                                Stock Mínimo
                            </label>
                            <Input
                                type="number"
                                value={filters.minStock}
                                onChange={(e) => handleFilterChange('minStock', e.target.value)}
                                placeholder="0"
                            />
                        </div>

                        {/* Stock Máximo */}
                        <div>
                            <label style={{ fontSize: '14px', color: '#555', fontWeight: '500', display: 'block', marginBottom: '5px' }}>
                                Stock Máximo
                            </label>
                            <Input
                                type="number"
                                value={filters.maxStock}
                                onChange={(e) => handleFilterChange('maxStock', e.target.value)}
                                placeholder="10"
                            />
                        </div>

                        {/* Nombre */}
                        <div>
                            <label style={{ fontSize: '14px', color: '#555', fontWeight: '500', display: 'block', marginBottom: '5px' }}>
                                Nombre
                            </label>
                            <Input
                                type="text"
                                value={filters.name}
                                onChange={(e) => handleFilterChange('name', e.target.value)}
                                placeholder="Buscar por nombre"
                            />
                        </div>

                        {/* Referencia */}
                        <div>
                            <label style={{ fontSize: '14px', color: '#555', fontWeight: '500', display: 'block', marginBottom: '5px' }}>
                                Referencia
                            </label>
                            <Input
                                type="text"
                                value={filters.reference}
                                onChange={(e) => handleFilterChange('reference', e.target.value)}
                                placeholder="Buscar por ref"
                            />
                        </div>

                        {/* Ordenar por */}
                        <div>
                            <label style={{ fontSize: '14px', color: '#555', fontWeight: '500', display: 'block', marginBottom: '5px' }}>
                                Ordenar por
                            </label>
                            <Select
                                options={sortByOptions}
                                value={sortByOptions.find(o => o.value === filters.sortBy)}
                                onChange={(option) => handleFilterChange('sortBy', option.value)}
                            />
                        </div>

                        {/* Orden */}
                        <div>
                            <label style={{ fontSize: '14px', color: '#555', fontWeight: '500', display: 'block', marginBottom: '5px' }}>
                                Orden
                            </label>
                            <Select
                                options={sortOrderOptions}
                                value={sortOrderOptions.find(o => o.value === filters.sortOrder)}
                                onChange={(option) => handleFilterChange('sortOrder', option.value)}
                            />
                        </div>

                        {/* Productos por página */}
                        <div>
                            <label style={{ fontSize: '14px', color: '#555', fontWeight: '500', display: 'block', marginBottom: '5px' }}>
                                Productos por página
                            </label>
                            <Select
                                options={limitOptions}
                                value={limitOptions.find(o => o.value === filters.limit)}
                                onChange={(option) => handleFilterChange('limit', option.value)}
                            />
                        </div>
                    </div>

                    {/* Botones de Filtros */}
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <Button
                            onClick={applyFilters}
                            style={{
                                backgroundColor: '#028322',
                                color: 'white',
                                padding: '10px 20px',
                                flex: 1
                            }}
                        >
                            Aplicar Filtros
                        </Button>
                        <Button
                            onClick={resetFilters}
                            outline
                            style={{
                                padding: '10px 20px',
                                flex: 1
                            }}
                        >
                            Resetear
                        </Button>
                    </div>
                </div>

                {/* Contador y Botones de Exportación */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                    flexWrap: 'wrap',
                    gap: '10px'
                }}>
                    <div style={{ fontSize: '14px', color: '#555' }}>
                        <strong>{selectedProducts.length}</strong> de <strong>{products.length}</strong> seleccionados
                        (Total: <strong>{pagination.totalProducts || 0}</strong> productos)
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <Button
                            onClick={exportToCSV}
                            style={{
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                padding: '8px 16px',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <FiDownload /> Exportar CSV
                        </Button>
                        <Button
                            onClick={printProducts}
                            style={{
                                backgroundColor: '#10b981',
                                color: 'white',
                                padding: '8px 16px',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <FiPrinter /> Imprimir
                        </Button>
                    </div>
                </div>

                {/* Tabla de Productos */}
                <UsersTable>
                    <UsersTableHead>
                        <UsersTableHeadColumn style={{ width: '50px' }}>
                            <input
                                type="checkbox"
                                checked={selectAll}
                                onChange={handleSelectAll}
                                style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                            />
                        </UsersTableHeadColumn>
                        <UsersTableHeadColumn>IMAGEN</UsersTableHeadColumn>
                        <UsersTableHeadColumn>REFERENCIA</UsersTableHeadColumn>
                        <UsersTableHeadColumn>PRODUCTO</UsersTableHeadColumn>
                        <UsersTableHeadColumn>PROVEEDOR</UsersTableHeadColumn>
                        <UsersTableHeadColumn>STOCK</UsersTableHeadColumn>
                        <UsersTableHeadColumn>PRECIO</UsersTableHeadColumn>
                        <UsersTableHeadColumn>ACCIONES</UsersTableHeadColumn>
                    </UsersTableHead>
                    <UsersTableBody>
                        {products.length !== 0 && products.map((product, index) => {
                            const isLowStock = product.stock < 10;
                            return (
                                <UsersTableRow key={index}>
                                    <UsersTableBodyColumn>
                                        <input
                                            type="checkbox"
                                            checked={selectedProducts.includes(product._id)}
                                            onChange={() => handleSelectProduct(product._id)}
                                            style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                        />
                                    </UsersTableBodyColumn>
                                    <UsersTableBodyColumn>
                                        <Zoom>
                                            <img
                                                src={product.images[0]}
                                                alt={product.name}
                                                style={{
                                                    width: '50px',
                                                    height: '50px',
                                                    objectFit: 'contain',
                                                    borderRadius: '5px',
                                                }}
                                                loading="lazy"
                                            />
                                        </Zoom>
                                    </UsersTableBodyColumn>
                                    <UsersTableBodyColumn>{product.reference || '-'}</UsersTableBodyColumn>
                                    <UsersTableBodyColumn
                                        onClick={() => {
                                            window.location.href = '/admin/product-edit/' + product._id;
                                        }}
                                        style={{ cursor: 'pointer', color: '#028322' }}
                                    >
                                        {product.name}
                                    </UsersTableBodyColumn>
                                    <UsersTableBodyColumn>{product.supplier || '-'}</UsersTableBodyColumn>
                                    <UsersTableBodyColumn>
                                        <Pill color={isLowStock ? '#dc3545' : '#028322'}>
                                            {product.stock}
                                        </Pill>
                                    </UsersTableBodyColumn>
                                    <UsersTableBodyColumn>
                                        <CurrencyFormat
                                            value={product.price}
                                            displayType={'text'}
                                            thousandSeparator={false}
                                            suffix={'€'}
                                            decimalSeparator=','
                                            decimalScale={2}
                                            fixedDecimalScale={true}
                                        />
                                    </UsersTableBodyColumn>
                                    <UsersTableBodyColumn>
                                        <Button
                                            onClick={() => {
                                                window.location.href = '/admin/product-edit/' + product._id;
                                            }}
                                            style={{
                                                backgroundColor: '#028322',
                                                color: 'white',
                                                padding: '6px 12px',
                                                fontSize: '14px'
                                            }}
                                        >
                                            EDITAR
                                        </Button>
                                    </UsersTableBodyColumn>
                                </UsersTableRow>
                            );
                        })}
                    </UsersTableBody>
                </UsersTable>

                {/* Mensaje si no hay productos */}
                {!loading && products.length === 0 && (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px',
                        color: '#999',
                        fontSize: '16px'
                    }}>
                        No se encontraron productos con los filtros aplicados
                    </div>
                )}
            </div>
        </>
    );
}
